/**
 * ONE vocabulary for what an order's payment status means on screen.
 *
 * Why this module exists (Sep 2026, independent review of PR #185)
 * ----------------------------------------------------------------
 * Three surfaces each carried their own copy of the payment-status
 * vocabulary — the admin orders list, the order details drawer, and the
 * drawer's own captured-amount allowlist — and they had drifted:
 *
 *   * PR #185 began writing a NEW order status, `cancelled`, from three
 *     paths (kiosk cancel, device cancel, TTL abandon). Neither label
 *     map knew it, and both fall back to `"Pending"` — so the abandoned
 *     kiosk attempt the PR was written to fix still read *Pending* on
 *     the tenant's Retail Orders screen, next to a drawer row that said
 *     "Payment cancelled".
 *   * the drawer's `CAPTURED_PAYMENT_STATUSES` correctly listed the
 *     Local-Delivery and tenant-review states where money IS collected,
 *     while its refund panel gate listed only `paid`/`partial_refund`.
 *     The same drawer therefore printed "Amount captured" and "no
 *     payment has been confirmed as collected" about one order, and
 *     hid the refund control on a genuinely captured Local Delivery
 *     order.
 *
 * Every consumer now reads the same three answers from here: what to
 * CALL a status, whether money was COLLECTED under it, and whether a
 * refund may be offered against it. None of this is authority — the
 * server decides refunds in `services/recorded_refunds.py` — but a
 * courtesy that contradicts itself is worse than none.
 */

/**
 * Statuses under which money HAS been collected.
 *
 * `paid` plus every Local-Delivery and tenant-review intermediate where
 * the charge has genuinely gone through and only an operational
 * decision is outstanding — see `retail/orders_state.py`'s own notes on
 * each, and `retail/payment_evidence.py::PAYMENT_EVIDENCE_STATUSES`,
 * which carries the same set on the server for the same reason.
 *
 * `refunded`/`partial_refund` are deliberately absent: money was
 * collected under both, but they need their own labels rather than
 * "Amount captured", so they are handled explicitly by callers and by
 * `isRefundablePaymentStatus` below.
 */
export const CAPTURED_PAYMENT_STATUSES = new Set([
  "paid",
  "captured",
  "payment_under_review",
  "awaiting_tenant_acceptance",
  "tenant_accepted",
  "paid_awaiting_test_dispatch_authorization",
  "test_dispatch_claimed",
  "captured_requires_tenant_review",
  "paid_fulfillment_pending",
]);

/** Money was returned, in whole or in part. */
export const REFUNDED_PAYMENT_STATUSES = new Set([
  "refunded",
  "partial_refund",
  "partially_refunded",
]);

/**
 * Statuses that may fund a refund: money was collected and has not all
 * been given back. The server nets confirmed collection against
 * confirmed refunds and is the authority; this only decides whether an
 * impossible control is worth rendering.
 */
export function isRefundablePaymentStatus(status) {
  const ps = normalizePaymentStatus(status);
  if (ps === "refunded") return false; // fully returned — nothing left
  return CAPTURED_PAYMENT_STATUSES.has(ps) || REFUNDED_PAYMENT_STATUSES.has(ps);
}

/** True when money was collected under this status, refunds included. */
export function isCollectedPaymentStatus(status) {
  const ps = normalizePaymentStatus(status);
  return CAPTURED_PAYMENT_STATUSES.has(ps) || REFUNDED_PAYMENT_STATUSES.has(ps);
}

export function normalizePaymentStatus(status) {
  return String(status || "").trim().toLowerCase();
}

/**
 * The tenant-facing label for each status.
 *
 * `cancelled` and `superseded` were the gap: PR #185 writes the first,
 * and `payments/engine.py` has always written the second, while both
 * label maps fell through to "Pending" for them.
 */
export const PAYMENT_STATUS_LABEL = {
  pending: "Pending",
  processing: "Processing",
  paid: "Paid",
  failed: "Failed",
  // Sep 2026 — a card attempt closed before any money was collected:
  // the customer cancelled at the reader, staff cancelled at the kiosk,
  // or the attempt lapsed unclaimed. Distinct from "Failed", which
  // means the card was presented and refused, and the difference is
  // what staff say to the customer.
  cancelled: "Cancelled — Not Paid",
  canceled: "Cancelled — Not Paid",
  // An attempt this codebase replaced with a newer one. Never money.
  superseded: "Superseded Attempt",
  refunded: "Fully Refunded",
  partial_refund: "Partially Refunded",
  partially_refunded: "Partially Refunded",
  // Kept as the established operator vocabulary, unchanged: the ops
  // watchdog, the alert types and the API error code all say
  // "reconciliation", and renaming it here would only fragment what
  // staff and an on-call responder are reading. The drawer's own
  // amount row already spells out what it means for the customer
  // ("Payment verification required — Stripe has not confirmed this
  // attempt").
  reconciliation_required: "Reconciliation Required",
  // The Local-Delivery and tenant-review intermediates all carry the
  // explicit "Payment Received — " prefix the DRAWER already used. That
  // wording exists for one reason: under every one of these the charge
  // has genuinely gone through and only an operational decision is
  // outstanding, so a label that omits it invites staff to read
  // captured money as unpaid. The orders LIST previously used shorter
  // forms ("Awaiting Your Acceptance"); unifying on the explicit ones
  // makes the list at least as truthful as the drawer, never less.
  awaiting_tenant_acceptance: "Payment Received — Awaiting Acceptance",
  tenant_accepted: "Payment Received — Requesting Courier",
  payment_under_review: "Payment Received — Verifying",
  paid_awaiting_test_dispatch_authorization:
    "Payment Received — Awaiting Dispatch Authorization",
  test_dispatch_claimed: "Payment Received — Requesting Courier",
  // Catalog-level Stripe authorization/tenant-review (Aug 2026) — the
  // customer's card is AUTHORIZED, never captured, at this point.
  // Never "Paid": no money has moved yet at "authorized".
  authorized: "Authorized — Awaiting Review",
  capture_processing: "Capturing…",
  authorization_release_processing: "Releasing Authorization…",
  authorization_released: "Authorization Released",
  paid_fulfillment_pending: "Paid — Fulfillment Pending",
  captured_requires_tenant_review: "Captured — Review Required",
};

export const PAYMENT_STATUS_COLOR = {
  pending: "bg-slate-100 text-slate-600",
  processing: "bg-slate-100 text-slate-600",
  paid: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-700",
  // Slate, not red: nothing went wrong and nothing was charged. Red is
  // reserved for a refusal or a refund, both of which need attention.
  cancelled: "bg-slate-200 text-slate-700",
  canceled: "bg-slate-200 text-slate-700",
  superseded: "bg-slate-200 text-slate-700",
  refunded: "bg-red-100 text-red-700 font-bold",
  partial_refund: "bg-orange-100 text-orange-800 font-bold",
  partially_refunded: "bg-orange-100 text-orange-800 font-bold",
  reconciliation_required: "bg-purple-100 text-purple-700 font-bold",
  awaiting_tenant_acceptance: "bg-indigo-100 text-indigo-700 font-bold",
  tenant_accepted: "bg-indigo-100 text-indigo-700",
  payment_under_review: "bg-indigo-100 text-indigo-700",
  paid_awaiting_test_dispatch_authorization: "bg-indigo-100 text-indigo-700 font-bold",
  test_dispatch_claimed: "bg-indigo-100 text-indigo-700",
  authorized: "bg-amber-100 text-amber-800 font-bold",
  capture_processing: "bg-slate-100 text-slate-600",
  authorization_release_processing: "bg-slate-100 text-slate-600",
  authorization_released: "bg-slate-200 text-slate-700",
  paid_fulfillment_pending: "bg-amber-100 text-amber-800 font-bold",
  captured_requires_tenant_review: "bg-purple-100 text-purple-700 font-bold",
};

/**
 * Financial-safety audit (Aug 2026) — `authorization_released` covers
 * two operationally distinct causes that share one payment_status: an
 * active tenant rejection (`payment_release_reason === "tenant_
 * rejected"`) versus a passive provider-side expiry
 * (`"provider_expired"`). Never conflate "I rejected this order" with
 * "I simply never acted in time."
 */
export function paymentStatusLabel(status, releaseReason) {
  const ps = normalizePaymentStatus(status);
  if (ps === "authorization_released" && releaseReason === "provider_expired") {
    return "Authorization Expired";
  }
  // An unrecognised status keeps the long-standing "Pending" fallback:
  // it is the honest reading of "we have no evidence of money", and it
  // is never applied to a status this file knows about.
  return PAYMENT_STATUS_LABEL[ps] || "Pending";
}

export function paymentStatusColor(status) {
  const ps = normalizePaymentStatus(status);
  return PAYMENT_STATUS_COLOR[ps] || PAYMENT_STATUS_COLOR.pending;
}
